import keys from '@debbie/common/dist/cache/keys.js';
import { CaseStatus } from '@debbie/common/dist/cases/index.js';
import { TimespanUnit } from '@debbie/common/dist/index.js';
import { caseFilterhash, } from '@debbie/common/dist/insights/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { objectBy } from '@debbie/common/dist/util/index.js';
import { PathType, TimingType, } from '@debbie/common/dist/workflows/index.js';
import * as insightsAPI from '@debbie/cortex/dist/api/insights.js';
import * as workflowStagesLib from '@debbie/cortex/dist/api/workflow-stages.js';
import { useEndReasons } from '@debbie/cortex/dist/end-reasons/index.js';
import { selectExclusiveContent } from '@debbie/cortex/dist/exclusiveContent/selectors.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { useMe } from '@debbie/cortex/dist/me/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useUpdateTypesMap } from '@debbie/cortex/dist/update-types/index.js';
import { useWorkflowStages, workflowStagesEntity, } from '@debbie/cortex/dist/workflow-stages/index.js';
import { useWorkflowStats, useWorkflowTaskStats, } from '@debbie/cortex/dist/workflows/index.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { errorNotification } from '../../utils/errors.js';
import Frown from '../Icons/Frown.js';
import { ExternalLink, X } from '../Icons/index.js';
import Meh from '../Icons/Meh.js';
import Smile from '../Icons/Smile.js';
import { Alert, Button, Checkbox, CopyToClipboard, Input, Loading, Modal, Panel, PlaceholderLoader, Select, Table, Well, } from '../index.js';
import UpdateTypeSettings from '../UpdateTypeSettings.js';
import RequirementsInput from './RequirementsInput.js';
import TimingInput from './TimingInput.js';
const NodeEditor = (props) => {
    const updateTypesMapLoadable = useUpdateTypesMap();
    const updateTypesMap = h.hasData(updateTypesMapLoadable)
        ? updateTypesMapLoadable.data
        : {};
    const lang = useSelector((s) => s.lang);
    const exclusiveContent = useSelector(selectExclusiveContent);
    const meLoadable = useMe();
    const me = h.hasData(meLoadable) ? meLoadable.data : null;
    const statsLoadable = useWorkflowStats(props.workflowId, {});
    const stats = h.hasData(statsLoadable) ? statsLoadable.data : null;
    const endReasons = useEndReasons();
    const endReasonLookup = h.hasData(endReasons)
        ? objectBy(endReasons.data, (er) => er.id)
        : {};
    const [redirecting, setRedirecting] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [updateType, setUpdateType] = useState(null);
    const { node } = props;
    const workflowStagesLoadable = useWorkflowStages();
    const workflowStages = h.hasData(workflowStagesLoadable)
        ? workflowStagesLoadable.data
        : null;
    // A public workflow is shared across tenants, so its tasks may only
    // reference public stages — the server rejects anything else on save
    const selectableStages = (workflowStages ?? []).filter((stage) => !props.publicWorkflow || stage.public);
    const createStageMutation = useMutation(async (label) => {
        const created = await workflowStagesLib.create({ label });
        q.invalidate(keys.write.create(workflowStagesEntity));
        return created;
    }, {
        onSuccess: (created) => {
            props.onChange(node.id, {
                workflowStageId: created.workflowStageId,
            });
        },
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.workflowStage);
        },
    });
    useEffect(() => {
        const _updateType = updateTypesMap[node.data.updateTypeId];
        if (_updateType) {
            setUpdateType(_updateType);
        }
    }, [updateTypesMap]);
    const taskStats = stats && stats.tasks.find((taskStats) => taskStats.taskId === node.id);
    const onChangeAlternativePaths = (index, change) => {
        props.onChange(node.id, {
            alternativePaths: node.data.alternativePaths.map((path, i) => {
                if (i === index) {
                    return {
                        ...path,
                        ...change,
                    };
                }
                else {
                    return path;
                }
            }),
        });
    };
    const goToCases = async (caseFilter) => {
        if (!redirecting[caseFilterhash(caseFilter)]) {
            setRedirecting({ ...redirecting, [caseFilterhash(caseFilter)]: true });
            const caseQueue = await insightsAPI.getCaseFilterQueue(caseFilter);
            setRedirecting({ ...redirecting, [caseFilterhash(caseFilter)]: false });
            c.redirect.redirect(`/cases/${caseQueue.join(',')}`);
        }
    };
    const currentCasesCaseFilter = {
        caseStatuses: [CaseStatus.VERIFIED],
        currentTaskIds: [node.id],
    };
    return (React.createElement(NodeEditorPanel, null,
        editModalOpen && updateType && (React.createElement(Modal, { header: lang.updateTypes[updateType.updateTypeId] || updateType.label, onToggle: () => setEditModalOpen(!editModalOpen) },
            React.createElement(UpdateTypeSettings, { updateTypeId: updateType.updateTypeId, preventDelete: true }))),
        React.createElement("h3", { style: { marginTop: 0 } }, me && exclusiveContent?.includes(me.id) ? (React.createElement(CopyToClipboard, { text: node.id }, lang.workflows.task)) : (lang.workflows.task)),
        props.editable && (React.createElement(Button, { block: true, size: "mini", theme: "secondary", onClick: () => setEditModalOpen(!editModalOpen) }, lang.updateTypesSettings.editUpdateType)),
        React.createElement("br", null),
        React.createElement(Well, null,
            React.createElement("h3", null, lang.workflowStages.stage),
            !node.data.workflowStageId && (React.createElement(Alert, { message: lang.workflowStages.missingStageWarning, size: "small" })),
            workflowStages ? (React.createElement(Select, { label: lang.workflowStages.selectStage, title: lang.workflowStages.stage, options: selectableStages.map((stage) => ({
                    value: stage.id,
                    label: stage.label,
                    description: stage.short ?? undefined,
                })), selected: node.data.workflowStageId ? [node.data.workflowStageId] : [], onChange: (selected) => props.onChange(node.id, {
                    workflowStageId: selected[0] ?? null,
                }), disabled: !props.editable || createStageMutation.isLoading, createOption: props.publicWorkflow
                    ? undefined
                    : {
                        label: lang.workflowStages.createNewStage,
                        onCreate: (search) => {
                            if (search.trim() !== '') {
                                createStageMutation.mutate(search.trim());
                            }
                        },
                    } })) : (React.createElement(PlaceholderLoader, null))),
        React.createElement("br", null),
        updateType && !(updateType.procedure && updateType.procedureEnabled) && (React.createElement(Well, null,
            React.createElement("h3", null, lang.workflows.pause),
            React.createElement(Checkbox, { placeholder: lang.workflows.pauseUnlimited, checked: !node.data.pauseAtTiming, onChange: (e) => {
                    if (e.target.checked) {
                        props.onChange(node.id, {
                            pauseAtTiming: null,
                        });
                    }
                    else {
                        props.onChange(node.id, {
                            pauseAtTiming: {
                                type: TimingType.Timespan,
                                details: {
                                    timespan: [0, TimespanUnit.Minutes],
                                },
                            },
                        });
                    }
                } }),
            node.data.pauseAtTiming && (React.createElement(TimingInput, { timing: node.data.pauseAtTiming, onChange: (timing) => props.onChange(node.id, {
                    pauseAtTiming: timing,
                }) })))),
        React.createElement("h3", null, lang.workflows.stats),
        React.createElement(TaskStats, { taskId: node.id, workflowId: props.workflowId }),
        taskStats && (React.createElement("div", null,
            React.createElement("h4", null, lang.workflows.endedCases),
            React.createElement(Table, { zebra: true, sumRow: true, style: { fontSize: '0.7em' } },
                taskStats.endedCases
                    .sort((a, b) => b.count - a.count)
                    .map((reason) => {
                    const caseFilter = {
                        caseStatuses: [CaseStatus.ENDED],
                        caseEndReasonIds: [reason.endReasonId],
                        currentTaskIds: [node.id],
                    };
                    const endReason = endReasonLookup[reason.endReasonId];
                    return (React.createElement("tr", null,
                        React.createElement("td", null, lang.endReason[endReason.handle] ??
                            endReason.label),
                        React.createElement("td", { style: { textAlign: 'right' } }, reason.count),
                        React.createElement("td", { style: { width: '30px' } }, redirecting[caseFilterhash(caseFilter)] ? (React.createElement(Loading, { style: {
                                width: '15px',
                                margin: '0px 1px -4px 1px',
                            } })) : (React.createElement(ExternalLink, { style: { cursor: 'pointer' }, onClick: () => {
                                goToCases(caseFilter);
                            } })))));
                }),
                React.createElement("tr", null,
                    React.createElement("td", null, lang.workflows.sum),
                    React.createElement("td", { style: { textAlign: 'right' } }, taskStats.endedCases.reduce((acc, reason) => reason.count + acc, 0)),
                    React.createElement("td", { style: { width: '30px' } }))),
            React.createElement("h4", null, lang.workflows.totalCases),
            React.createElement(Table, { zebra: true, style: { fontSize: '0.7em' } },
                React.createElement("tr", null,
                    React.createElement("td", null, lang.workflows.sum),
                    React.createElement("td", { style: { textAlign: 'right' } }, taskStats.totalCases),
                    React.createElement("td", null))),
            React.createElement("h4", null, lang.workflows.currentCases),
            React.createElement(Table, { zebra: true, style: { fontSize: '0.7em' } },
                React.createElement("tr", null,
                    React.createElement("td", null, lang.workflows.sum),
                    React.createElement("td", { style: { textAlign: 'right' } }, taskStats.currentTaskCases),
                    React.createElement("td", { style: { width: '30px' } }, !!taskStats.currentTaskCases &&
                        (redirecting[caseFilterhash(currentCasesCaseFilter)] ? (React.createElement(Loading, { style: {
                                width: '15px',
                                margin: '0px 1px -4px 1px',
                            } })) : (React.createElement(ExternalLink, { style: { cursor: 'pointer' }, onClick: () => {
                                goToCases(currentCasesCaseFilter);
                            } })))))))),
        (props.editable || !!node.data.alternativePaths.length) && (React.createElement("h3", null, lang.workflows.alternativePaths)),
        node.data.alternativePaths.map((path, index) => (React.createElement(Well, { key: path.id, style: { marginBottom: '10px' } },
            React.createElement("h4", null,
                lang.workflows.alternativePath,
                ' ',
                props.editable && (React.createElement(X, { style: { float: 'right', cursor: 'pointer' }, onClick: () => props.onChange(node.id, {
                        alternativePaths: node.data.alternativePaths.filter((_, i) => i !== index),
                    }) }))),
            React.createElement("h4", null, lang.workflows.source),
            React.createElement(Input, { placeholder: lang.workflows.source, value: path.source, onChange: (e) => onChangeAlternativePaths(index, {
                    source: e.target.value,
                }) }),
            React.createElement("h4", null, lang.workflows.priority),
            React.createElement(Input, { placeholder: lang.workflows.priority, type: "number", value: path.priority, onChange: (e) => {
                    const val = e.target.value;
                    const isNumber = !isNaN(parseInt(val));
                    if (isNumber) {
                        onChangeAlternativePaths(index, {
                            priority: parseInt(val),
                        });
                    }
                } }),
            React.createElement("h4", null, lang.workflows.timing),
            React.createElement(TimingInput, { timing: path.timing, onChange: (timing) => onChangeAlternativePaths(index, {
                    timing,
                }) }),
            React.createElement("h4", null,
                lang.workflows.requirements,
                " "),
            React.createElement(RequirementsInput, { requirements: path.requirements, onChange: (requirements) => onChangeAlternativePaths(index, {
                    requirements,
                }) })))),
        props.editable && (React.createElement(Button, { theme: "secondary", block: true, size: "small", onClick: () => props.onChange(node.id, {
                alternativePaths: node.data.alternativePaths.concat({
                    type: PathType.ForMigration,
                    id: crypto.randomUUID(),
                    manual: false,
                    priority: 1,
                    source: 'from id',
                    target: node.id,
                    requirements: [],
                    timing: {
                        type: TimingType.Timespan,
                        details: {
                            timespan: [12, TimespanUnit.Days],
                        },
                    },
                }),
            }) }, lang.workflows.addAlternativePath))));
};
export default NodeEditor;
const NodeEditorPanel = styled(Panel) `
  position: absolute;
  width: 300px;
  z-index: 9;
  left: 40px;
  top: 80px;

  .body {
    max-height: calc(100vh - 240px);
    overflow-y: scroll;
  }
`;
const TaskStats = (props) => {
    const lang = useSelector((s) => s.lang);
    const workflowTaskStatsLoadable = useWorkflowTaskStats(props.workflowId, props.taskId, {});
    const workflowTaskStats = h.hasData(workflowTaskStatsLoadable)
        ? workflowTaskStatsLoadable.data
        : null;
    return workflowTaskStats && !h.isLoading(workflowTaskStatsLoadable) ? (React.createElement("div", null,
        !!Object.entries(workflowTaskStats.incomingInteractions).length && (React.createElement(React.Fragment, null,
            React.createElement("h4", null, lang.workflows.incomingInteractionsStats),
            React.createElement(Table, { zebra: true, style: { fontSize: '0.7em' } }, Object.entries(workflowTaskStats.incomingInteractions).map(([interactionType, count]) => (React.createElement("tr", null,
                React.createElement("td", null, lang.interactionTypes[interactionType]),
                React.createElement("td", { style: { textAlign: 'right' } }, count))))))),
        React.createElement("h4", null, lang.workflows.paymentPlansCreatedStats),
        React.createElement(Table, { zebra: true, style: { fontSize: '0.7em' } },
            React.createElement("tr", null,
                React.createElement("td", null, workflowTaskStats.paymentPlansCreated || 0))),
        !!workflowTaskStats.satisfactions.reduce((acc, s) => acc + s.count, 0) && (React.createElement(React.Fragment, null,
            React.createElement("h4", null, lang.workflows.satisfactionStats),
            React.createElement("div", { style: { textAlign: 'center' } },
                React.createElement(SatisfactionStat, { count: workflowTaskStats.satisfactions.find((s) => s.satisfaction === 1)?.count },
                    React.createElement(Frown, { style: {
                            width: '30px',
                            height: '30px',
                        } })),
                React.createElement(SatisfactionStat, { count: workflowTaskStats.satisfactions.find((s) => s.satisfaction === 3)?.count },
                    React.createElement(Meh, { style: {
                            width: '30px',
                            height: '30px',
                        } })),
                React.createElement(SatisfactionStat, { count: workflowTaskStats.satisfactions.find((s) => s.satisfaction === 5)?.count },
                    React.createElement(Smile, { style: {
                            width: '30px',
                            height: '30px',
                        } }))))))) : (React.createElement(PlaceholderLoader, null));
};
const SatisfactionStat = (props) => {
    return (React.createElement(StyledSatisfaction, null,
        props.children,
        React.createElement("div", null, props.count || 0)));
};
const StyledSatisfaction = styled.div `
  display: inline-block;
  margin-right: 8px;
  text-align: center;
  font-size: 0.7em;
`;
//# sourceMappingURL=NodeEditor.js.map